// FÁZE 4 — Review: pool 1/3/8 GW, focus/decision review, BKSA přehled.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { reviewPool, reviewFocuses, reviewBksa, patchPoolEntry, removePoolEntry } from "../../lib/lab-api";
import ContextArticles from "../../components/ContextArticles";

const VERDICT = {
    fulfilled: ["Fulfilled", "text-fa-mint border-fa-mint/50"],
    partially_fulfilled: ["Partially Fulfilled", "text-fa-yellow border-fa-yellow/50"],
    not_fulfilled: ["Not Fulfilled", "text-red-300 border-red-400/50"],
    unable: ["Unable to Evaluate", "text-white/40 border-white/20"],
};

export default function LabReview() {
    const { user, login } = useAuth();
    const [horizon, setHorizon] = useState(3);
    const [pool, setPool] = useState(null);
    const [focuses, setFocuses] = useState(null);
    const [bksa, setBksa] = useState(null);

    const load = useCallback(() => {
        if (!user) return;
        reviewPool(horizon).then(setPool).catch(() => {});
        reviewFocuses().then(setFocuses).catch(() => {});
        reviewBksa().then(setBksa).catch(() => {});
    }, [user, horizon]);
    useEffect(() => { load(); }, [load]);

    const apply = async (row) => {
        try {
            if (row.suggestion_target === "remove") await removePoolEntry(row.entry_id);
            else await patchPoolEntry(row.entry_id, { status: row.suggestion_target });
            toast.success(`${row.name}: ${row.suggestion_label} ✓`);
            load();
        } catch { toast.error("Aplikace návrhu selhala"); }
    };

    if (!user) return (
        <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/60" data-testid="review-login-cta">
            Pro review se <button type="button" onClick={login} className="text-fa-mint underline">přihlas</button>.
        </div>
    );

    return (
        <div className="space-y-8" data-testid="lab-review">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">Období:</span>
                {[1, 3, 8].map((h) => (
                    <button key={h} type="button" onClick={() => setHorizon(h)} data-testid={`review-horizon-${h}`}
                            className={`font-mono text-[10px] uppercase px-3 py-1.5 border ${horizon === h ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/50 hover:text-white"}`}>
                        {h} GW
                    </button>
                ))}
                {pool?.window && (
                    <span className="font-mono text-[9px] text-white/40 ml-2" data-testid="review-window">
                        {pool.window.season} · GW{pool.window.gw_from}–{pool.window.gw_to}
                    </span>
                )}
            </div>

            {/* Pool review */}
            <section className="space-y-3">
                <h2 className="font-display font-black uppercase text-base flex items-center gap-2">
                    <ArrowCounterClockwise size={16} className="text-fa-mint" /> Pool Review
                </h2>
                {!pool ? <p className="font-mono text-[10px] text-white/40">Načítám…</p> :
                 pool.rows.length === 0 ? (
                    <div className="border border-white/10 bg-black/40 p-4 font-mono text-[10px] text-white/45" data-testid="review-pool-empty">
                        Pool je prázdný — přidej hráče ve <Link to="/lab/scout" className="text-fa-mint underline">Scout</Link>.
                    </div>
                 ) : (
                    <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="review-pool-table">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                                    <th className="px-3 py-2">Hráč</th><th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2 text-right">Body</th><th className="px-3 py-2 text-right">PPG</th>
                                    <th className="px-3 py-2 text-right">Minuty</th><th className="px-3 py-2">Doporučení</th><th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {pool.rows.map((r) => (
                                    <tr key={r.entry_id} className="border-b border-white/5 font-mono text-[11px]" data-testid={`review-row-${r.fpl_id}`}>
                                        <td className="px-3 py-2">
                                            <Link to={`/players/${r.fpl_id}`} className="font-display font-bold hover:text-fa-mint">{r.name}</Link>
                                            <span className="text-white/35 text-[9px] ml-1.5">{r.team} · {r.position}</span>
                                        </td>
                                        <td className="px-3 py-2 uppercase text-[9px] text-fa-sky">{r.status}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-bold text-fa-mint">{r.points ?? "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.ppg ?? "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-white/50">{r.minutes ?? "—"}</td>
                                        <td className="px-3 py-2 text-[10px] text-white/70">{r.suggestion_label}{!r.has_data && <span className="text-white/30"> (bez dat)</span>}</td>
                                        <td className="px-3 py-2">
                                            {r.apply_needed && (
                                                <button type="button" onClick={() => apply(r)} data-testid={`review-apply-${r.fpl_id}`}
                                                        className="font-mono text-[8px] uppercase px-2 py-1 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                                                    Aplikovat
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                 )}
                <p className="font-mono text-[9px] text-white/40">Návrhy jen doporučují — status se změní až po tvém potvrzení.</p>
            </section>

            {/* Focus review */}
            <section className="space-y-3">
                <h2 className="font-display font-black uppercase text-base">Recruitment Focus & Decision Review</h2>
                {!focuses ? <p className="font-mono text-[10px] text-white/40">Načítám…</p> :
                 focuses.items.length === 0 ? (
                    <div className="border border-white/10 bg-black/40 p-4 font-mono text-[10px] text-white/45" data-testid="review-focus-empty">
                        Zatím žádný focus s Final Selection — dokonči výběr v <Link to="/lab/select" className="text-fa-mint underline">Select</Link>.
                    </div>
                 ) : (
                    <div className="grid md:grid-cols-2 gap-3" data-testid="review-focus-list">
                        {focuses.items.map((f) => {
                            const [vl, vc] = VERDICT[f.verdict] || VERDICT.unable;
                            return (
                                <div key={f.focus_id} className="border border-white/10 bg-black/40 p-4 space-y-2" data-testid={`review-focus-${f.focus_id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-display font-bold text-sm">{f.title}</div>
                                        <span className={`font-mono text-[8px] uppercase px-1.5 py-0.5 border ${vc}`}>{vl}</span>
                                    </div>
                                    <div className="font-mono text-[9px] text-white/45 uppercase">GW{f.gw_start}–{f.gw_end} · vybrán: {f.selected_name || "—"}</div>
                                    {f.selected && (
                                        <div className="font-mono text-[10px] text-white/70">
                                            {f.selected.points} bodů · PPG {f.selected.ppg} · {f.selected.minutes} min
                                            {f.alternatives?.length > 0 && ` · alternativy: ${f.alternatives.map((a) => `${a.player_key} ${a.points}b`).join(", ")}`}
                                        </div>
                                    )}
                                    {f.decision_quadrant && (
                                        <div className="font-mono text-[9px] text-fa-cream border-l-2 border-fa-cream/50 pl-2">{f.decision_quadrant}</div>
                                    )}
                                    {f.decision_reason && <div className="font-mono text-[9px] text-white/40">Důvod: „{f.decision_reason}"</div>}
                                </div>
                            );
                        })}
                    </div>
                 )}
            </section>

            {/* BKSA review */}
            <section className="space-y-3">
                <h2 className="font-display font-black uppercase text-base">BKSA Review</h2>
                {!bksa ? <p className="font-mono text-[10px] text-white/40">Načítám…</p> : (
                    <div className="flex flex-wrap gap-2 font-mono text-[10px]" data-testid="review-bksa-stats">
                        {[["Manager Rating", bksa.manager_rating, "text-fa-mint"], ["Knowledge XP", bksa.knowledge_xp, "text-fa-sky"],
                          ["Vyhodnoceno", bksa.resolved, "text-white/70"], ["Trefy", bksa.hits, "text-fa-yellow"], ["Streak", bksa.streak, "text-fa-cream"]].map(([l, v, c]) => (
                            <div key={l} className="border border-white/10 bg-black/40 px-3 py-2">
                                <div className="text-[8px] uppercase text-white/40">{l}</div>
                                <div className={`font-display font-black text-lg tabular-nums ${c}`}>{v ?? 0}</div>
                            </div>
                        ))}
                        <Link to="/bksa" className="border border-fa-yellow/40 text-fa-yellow px-3 py-2 flex items-center gap-1.5 uppercase text-[9px] hover:bg-fa-yellow hover:text-black transition-colors">
                            <CheckCircle size={12} /> Vyplnit predikce
                        </Link>
                    </div>
                )}
                {bksa?.items?.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="review-bksa-entries">
                        {bksa.items.slice(0, 8).map((e) => (
                            <div key={e.id} className="border border-white/10 bg-black/40 p-3 font-mono text-[10px] space-y-1">
                                <div className="uppercase text-[8px] text-white/40">GW{e.gameweek} · {e.category.toUpperCase()} · {e.horizon} GW</div>
                                <div className="font-display font-bold text-sm">{e.player_name}</div>
                                {e.results
                                    ? <div className={e.results.hit ? "text-fa-mint" : "text-red-300"}>
                                        {e.results.hit ? "✓ Trefa (+5 MR)" : "✗ Vedle"} · {e.results.points}b (PPG {e.results.ppg})
                                      </div>
                                    : <div className="text-white/40">čeká na vyhodnocení</div>}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <ContextArticles category="strategy" title="Z Academy: jak vyhodnocovat rozhodnutí" />
        </div>
    );
}
